import React from 'react';
import styled from 'styled-components';
import { ConnectButton } from '@rainbow-me/rainbowkit';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(8px);
`;

const ModalContainer = styled.div`
  background: rgba(42, 31, 61, 0.5);
  border-radius: 24px;
  padding: 40px;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(149, 53, 252, 0.3);
  position: relative;
  animation: slideIn 0.3s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: #888;
  font-size: 24px;
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #ffffff;
  }
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 24px;
  background: linear-gradient(135deg, #9535fc, #7c3aed);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  box-shadow: 0 8px 24px rgba(149, 53, 252, 0.4);
  
  img {
    width: 50px;
    height: 50px;
    object-fit: contain;
  }
`;

const Title = styled.h2`
  font-family: 'Inter', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #ffffff;
  text-align: center;
  margin-bottom: 12px;
`;

const Subtitle = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: #b0b0b0;
  text-align: center;
  margin-bottom: 32px;
  line-height: 1.6;
`;

const ConnectButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  
  /* Style the RainbowKit button */
  button {
    font-family: 'Inter', sans-serif !important;
    font-weight: 600 !important;
    font-size: 16px !important;
    padding: 16px 32px !important;
    border-radius: 12px !important;
    background: rgba(88, 28, 135, 0.8) !important;
    transition: all 0.2s ease !important;
    
    &:hover {
      background: rgba(88, 28, 135, 1) !important;
    }
  }
`;

const TermsText = styled.p`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  color: #888;
  text-align: center;
  margin-top: 16px;
  line-height: 1.4;
`;

const SkipButton = styled.button`
  background: transparent;
  border: 2px solid #444;
  color: #888;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 12px;
  cursor: pointer;
  width: 100%;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #666;
    color: #b0b0b0;
    background: rgba(255, 255, 255, 0.05);
  }
`;

const FeatureList = styled.div`
  background: rgba(149, 53, 252, 0.1);
  border: 1px solid rgba(149, 53, 252, 0.3);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
`;

const Feature = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #d0d0d0;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FeatureIcon = styled.span`
  margin-right: 12px;
  font-size: 18px;
`;

interface WalletPromptModalProps {
  onClose: () => void;
}

const WalletPromptModal: React.FC<WalletPromptModalProps> = ({ onClose }) => {
  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <IconContainer>
          <img src="/mavrk_logo_white.png" alt="Mavrk" />
        </IconContainer>
        
        <Title>CONNECT YOUR WALLET</Title>
        <Subtitle>
          Connect your wallet to enable our entire platform experience.
        </Subtitle>
        
        <FeatureList>
          <Feature>
            <FeatureIcon>•</FeatureIcon>
            Free token creations
          </Feature>
          <Feature>
            <FeatureIcon>•</FeatureIcon>
            25% Creator Earnings on LP trading fees
          </Feature>
          <Feature>
            <FeatureIcon>•</FeatureIcon>
            Bridge funds across multiple chains
          </Feature>
          <Feature>
            <FeatureIcon>•</FeatureIcon>
            Cross-chain token swaps
          </Feature>
        </FeatureList>
        
        <ConnectButtonContainer>
          <ConnectButton />
        </ConnectButtonContainer>
        
        <TermsText>
          *I agree to the platform terms and conditions by connecting my wallet or skipping
        </TermsText>
        
        <SkipButton onClick={onClose}>
          Skip for now
        </SkipButton>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default WalletPromptModal;

